import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Img from '@/components/shared/Img';

const services = [
  {
    title: 'Kitchen Remodeling',
    summary: 'Full gut renovations to targeted upgrades — modern kitchens that blend function with craftsmanship.',
    href: '/services/kitchen-remodeling',
    image: '/images/65663e3b0_generated_f029db7f.png',
  },
  {
    title: 'Bathroom Remodeling',
    summary: 'Custom tile work, walk-in showers, luxury vanities — bathrooms built to impress and built to last.',
    href: '/services/bathroom-remodeling',
    image: '/images/3aec1df9c_generated_7bf54895.png',
  },
  {
    title: 'Home Additions & Expansions',
    summary: 'More space, better layout — room additions and open-concept conversions done right.',
    href: '/services/home-additions',
    image: '/images/fac8dc0e8_figures-house-caliper-drafts.jpg',
  },
  {
    title: 'Custom Builds & Structural Work',
    summary: 'Framing, layout reworks, and structural builds — the bones that make everything else possible.',
    href: '/services/custom-builds',
    image: '/images/cdbed6814_custombuilds.jpg',
  },
  {
    title: 'Interior Finishes & Custom Accents',
    summary: 'Staircases, built-ins, wood accents, and architectural details — the details that make a house feel custom.',
    href: '/services/interior-finishes',
    image: '/images/117d55f5b_656918942_122115725193221049_1636339166466878223_n.jpg',
  },
  {
    title: 'Roofing',
    summary: 'Full replacements, storm damage repair, and coastal wind-zone compliant installations — done right.',
    href: '/services/roofing',
    image: '/images/c5694eaa3_Roofing.jpg',
  },
  {
    title: 'Flooring',
    summary: 'Hardwood, LVP, and tile — installed over properly prepared subfloors that make the difference.',
    href: '/services/flooring',
    image: '/images/cf3337620_flooring.jpg',
  },
  {
    title: 'Painting',
    summary: 'Professional interior and exterior painting with coastal-grade materials and proper prep work.',
    href: '/services/painting',
    image: '/images/dae00480b_painting.jpg',
  },
  {
    title: 'Home Systems & Upgrades',
    summary: 'Water heaters, generators, electrical upgrades, and plumbing — the systems that keep your home running.',
    href: '/services/home-systems-upgrades',
    image: '/images/33cad0df0_630532657_1669404440725600_8366413664056088405_n.jpg',
  },
  {
    title: 'Commercial Remodeling',
    summary: 'Office, retail, and restaurant renovations — residential-quality finishes without the commercial overhead.',
    href: '/services/commercial-remodeling',
    image: '/images/c0efeadc9_carpentry-workspace.jpg',
  },
  {
    title: 'EV Charger Installation',
    summary: 'Professional Level 2 EV charger installation — all brands, code-compliant, done in a day.',
    href: '/services/ev-charger-installation',
    image: '/images/c544d8b7a_generated_fe47dd1d.png',
  },
  {
    title: 'ADA Compliance & Accessibility',
    summary: 'Roll-in showers, grab bars, ramps, and accessible entries — built with dignity and craftsmanship.',
    href: '/services/ada-compliance',
    image: '/images/79127eb42_ada-compliant-restrooms.jpg',
  },
];

export default function ServicesEditorial() {
  return (
    <section className="py-24 md:py-32 bg-[#111111] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="w-12 h-px bg-[#C97B2E] mx-auto mb-6" />
          <span className="font-body text-[#C97B2E] text-xs font-bold uppercase tracking-[0.3em] mb-4 block">
            What We Do
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Remodeling & Construction
            <br className="hidden md:block" />
            <span className="italic">Across the Coastal Bend</span>
          </h2>
          <p className="font-body text-white/55 text-base md:text-lg max-w-2xl mx-auto">
            From kitchens and bathrooms to roofing, accessibility, and commercial remodeling — every project
            gets the same preparation, communication, and finish quality.
          </p>
        </motion.div>

        {/* Grid — 12 services = 4 rows of 3, perfectly even */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-white/10">
          {services.map((s, i) => {
            const num = String(i + 1).padStart(2, '0');
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                className="group relative border-b border-r border-white/10 flex flex-col overflow-hidden"
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <Img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                    sizes="(min-width: 1280px) 400px, (min-width: 1024px) 31vw, (min-width: 640px) 46vw, calc(100vw - 2.5rem)"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-[#111111]" />
                  <span
                    className="font-heading font-bold text-white select-none pointer-events-none absolute top-3 right-4"
                    style={{ fontSize: '4rem', lineHeight: 1, opacity: 0.15 }}
                    aria-hidden="true"
                  >
                    {num}
                  </span>
                </div>

                {/* Text content */}
                <div className="p-7 md:p-8 flex flex-col flex-1">
                  <span className="font-body text-[#C97B2E] text-xs font-bold tracking-[0.3em] mb-3 block">
                    {num}
                  </span>
                  <h3 className="font-heading text-white text-xl font-bold mb-3 leading-snug">
                    {s.title}
                  </h3>
                  <p className="font-body text-white/50 text-sm leading-relaxed mb-5 flex-1">
                    {s.summary}
                  </p>
                  <div>
                    <div className="h-px bg-[#C97B2E] mb-3 transition-all duration-500 w-0 group-hover:w-10" />
                    <Link
                      to={s.href}
                      className="inline-flex items-center gap-2 font-body text-[#C97B2E] text-xs font-bold uppercase tracking-wider hover:text-white transition-colors group/link"
                    >
                      Learn More
                      <span className="sr-only"> about {s.title}</span>
                      <ArrowRight className="w-3 h-3 group-hover/link:translate-x-1 transition-transform" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-14"
        >
          <Link
            to="/services"
            className="inline-flex items-center gap-3 border-2 border-white/20 text-white px-8 py-4 rounded-sm font-body font-bold uppercase tracking-wider text-sm hover:border-[#C97B2E] hover:text-[#C97B2E] transition-all duration-300 group"
          >
            View All Services
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}